import { NextSeo } from 'next-seo'
import React from 'react'
import { ISbStoryData } from 'storyblok-js-client'

import { DISALLOW_ROBOTS, SITE_ORIGIN } from 'lib/constants'
import { useDataContext } from 'lib/contexts/dataContext'
import { removeTrailingSlash } from 'lib/utils/string'

export const Seo = ({ story }: { story: ISbStoryData }): JSX.Element => {
  const noIndex = DISALLOW_ROBOTS || story.content?.should_index === 'no'
  const { regions, alternates } = useDataContext()

  const getHref = (region: string): string => {
    const currentRegion = story.full_slug.split('/')[0]
    if (region === currentRegion) {
      return removeTrailingSlash(`${SITE_ORIGIN}${story.full_slug}`)
    }

    const alternate = alternates?.find((alt) => {
      const altRegion = alt.split('/')[0]
      return region === altRegion
    })

    return removeTrailingSlash(`${SITE_ORIGIN}${alternate || region}`)
  }

  const getHeroImage = () => {
    if (!story.content || !story.content.body) {
      return null
    }

    const hero = story.content.body.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (block: any) => block.component === 'new-hero'
    )

    if (hero) {
      return hero.image
    }
    return null
  }

  return (
    <NextSeo
      title={story.content?.seo?.title}
      description={story.content?.seo?.description}
      canonical={
        story.content?.canonical_url ||
        removeTrailingSlash(`${SITE_ORIGIN}${story.full_slug}`)
      }
      noindex={noIndex}
      nofollow={noIndex}
      openGraph={{
        type: 'website',
        url: removeTrailingSlash(`${SITE_ORIGIN}${story.full_slug}`),
        title:
          story.content?.seo?.og_title ||
          story.content?.seo?.twitter_title ||
          story.content?.seo?.title,
        description:
          story.content?.seo?.og_description ||
          story.content?.seo?.twitter_description ||
          story.content?.seo?.description,
        images: getHeroImage()
          ? [{ url: getHeroImage().filename }]
          : story.content?.seo?.og_image || story.content?.seo?.twitter_image
          ? [
              {
                // we're not adding both because probably they will be the same and it's not
                // very professional to have two very similar images
                url: (story.content?.seo?.og_image ||
                  story.content?.seo?.twitter_image) as string,
              },
            ]
          : [{ url: `${SITE_ORIGIN}og.png` }],
      }}
      languageAlternates={[
        {
          hrefLang: 'x-default',
          href: SITE_ORIGIN,
        },
        ...(regions || []).map((region) => ({
          hrefLang: region.code,
          href: getHref(region.code),
        })),
      ]}
    />
  )
}
